import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { WhatsAppOutlined } from "@ant-design/icons";
import { LOCATION_BY_SLUG, REDIRECTS } from "../../data/locations";
import HeroImg from "../../Comon/Images/GarvanaHome4.jpg";
import "./LocationPage.css";

const BASE_URL = "https://www.garvana.com";
const PHONE = "+919800040505";
const WA_MSG = "Hi Garvaana, I'd like a free consultation for landscape/garden services.";

export default function LocationPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const loc = LOCATION_BY_SLUG[`/${slug}`];

  /* Old separate garden-services URLs → redirect to the combined city page */
  if (!loc) {
    const redirect = REDIRECTS[`/${slug}`];
    if (redirect) return <Navigate to={redirect} replace />;
    return <Navigate to="/" replace />;
  }

  const fullUrl = `${BASE_URL}/${loc.slug}`;

  /* ── Structured data: a city-scoped Service + FAQ, helps local + rich results ── */
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Landscape Design & Garden Management",
    provider: {
      "@type": "LocalBusiness",
      name: "Garvaana Enterprise",
      telephone: "+91-9800040505",
      url: BASE_URL,
    },
    areaServed:
      loc.city === "India"
        ? { "@type": "Country", name: "India" }
        : { "@type": "City", name: loc.city },
    name: loc.h1,
    description: loc.description,
    url: fullUrl,
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: loc.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
      { "@type": "ListItem", position: 2, name: loc.h1, item: fullUrl },
    ],
  };

  return (
    <div className="loc-page">
      <Helmet>
        <title>{loc.title}</title>
        <meta name="description" content={loc.description} />
        <meta name="keywords" content={loc.keywords} />
        <link rel="canonical" href={fullUrl} />
        <meta property="og:title" content={loc.title} />
        <meta property="og:description" content={loc.description} />
        <meta property="og:url" content={fullUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* ── HERO ── */}
      <section className="loc-hero">
        <img src={HeroImg} alt={`Landscape design in ${loc.city} by Garvaana Enterprise`} className="loc-hero__img" />
        <div className="loc-hero__overlay" />
        <div className="loc-hero__content">
          <span className="loc-hero__label">✦ {loc.heroLabel}</span>
          <h1 className="loc-hero__title">{loc.h1}</h1>
          <p className="loc-hero__sub">
            Luxury landscape design, garden installation & maintenance by Garvaana Enterprise
          </p>
          <div className="loc-hero__actions">
            <a href={`tel:${PHONE}`} className="btn-luxury loc-rbtn">
              📞 Call +91 98000 40505
            </a>
            <a
              href={`https://wa.me/${PHONE.replace("+", "")}?text=${encodeURIComponent(WA_MSG)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="wa-btn"
            >
              <WhatsAppOutlined /> WhatsApp Us
            </a>
          </div>
          <button className="loc-hero__quote" onClick={() => navigate("/contact")}>
            Prefer a form? Request a free quote →
          </button>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section className="loc-section loc-intro">
        <span className="section-label">Landscape &amp; Garden Services · {loc.city}</span>
        <h2 className="section-title">
          {loc.city}'s Trusted Landscape &amp; Garden Company
        </h2>
        <span className="gold-divider" />
        {loc.intro.map((p, i) => (
          <p key={i} className="loc-intro__p">{p}</p>
        ))}
      </section>

      {/* ── LANDSCAPE DESIGN SERVICES (card grid) ── */}
      <section className="loc-section loc-services" id="landscape-design">
        <div className="loc-services__head">
          <span className="section-label">Design &amp; Build · {loc.city}</span>
          <h2 className="section-title">Landscape Design in {loc.city}</h2>
          <span className="gold-divider center" />
        </div>
        <div className="loc-cards">
          {loc.designServices.map((s, i) => (
            <div className="loc-card" key={i}>
              <span className="loc-card__num">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="loc-card__title">{s}</h3>
            </div>
          ))}
        </div>

        {loc.localities && (
          <div className="loc-areas">
            <h3 className="loc-areas__title">Areas We Cover in {loc.city}</h3>
            <div className="loc-areas__chips">
              {loc.localities.map((a, i) => (
                <span className="loc-areas__chip" key={i}>{a}</span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ── GARDEN MAINTENANCE / AMC (card grid) ── */}
      {loc.gardenServices && (
        <section className="loc-section loc-services loc-services--alt" id="garden-maintenance">
          <div className="loc-services__head">
            <span className="section-label">Care &amp; AMC · {loc.city}</span>
            <h2 className="section-title">Garden Maintenance in {loc.city}</h2>
            <span className="gold-divider center" />
            <p className="loc-services__lead">
              Keep your garden flawless all year with our monthly, quarterly &amp; annual
              maintenance contracts (AMC) — for homes, villas, societies &amp; commercial spaces.
            </p>
          </div>
          <div className="loc-cards">
            {loc.gardenServices.map((s, i) => (
              <div className="loc-card" key={i}>
                <span className="loc-card__num">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="loc-card__title">{s}</h3>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── FEATURED PROJECT ── */}
      {loc.featuredProject && (
        <section className="loc-section loc-project">
          <span className="section-label">Featured Work</span>
          <h2 className="loc-block-title">{loc.featuredProject.name}</h2>
          <span className="gold-divider" />
          <p className="loc-intro__p">{loc.featuredProject.blurb}</p>
          <Link to="/projects" className="loc-textlink">View our full portfolio →</Link>
        </section>
      )}

      {/* ── COMPLETE GARDEN SERVICES — pillars band (all pages) ── */}
      <section className="loc-pillars">
        <div className="loc-pillars__inner">
          <span className="section-label" style={{ color: "var(--color-accent-light)" }}>
            One Partner · Every Green Need
          </span>
          <h2 className="loc-pillars__title">All Garden Services Under One Roof</h2>
          <div className="loc-pillars__grid">
            {[
              { t: "Landscape Design", d: "Bespoke 3D garden & landscape design for villas, terraces, farmhouses & commercial spaces." },
              { t: "Installation & Build", d: "Lawns, hardscape, irrigation, lighting, water features & vertical green walls." },
              { t: "Maintenance & AMC", d: "Monthly, quarterly & annual garden care — pruning, lawn care & pest management." },
              { t: "Plant Nursery", d: "500+ plant varieties from our own nursery — wholesale & retail supply." },
            ].map((p, i) => (
              <div className="loc-pillar" key={i}>
                <span className="loc-pillar__mark">✦</span>
                <h3 className="loc-pillar__title">{p.t}</h3>
                <p className="loc-pillar__desc">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY GARVAANA — trust section (all pages) ── */}
      <section className="loc-section loc-why">
        <div className="loc-why__head">
          <span className="section-label">Why Garvaana</span>
          <h2 className="section-title">The Garvaana Difference</h2>
          <span className="gold-divider center" />
        </div>
        <div className="loc-why__grid">
          {[
            { t: "Bespoke Design", d: "Every garden tailored to your space, taste and lifestyle — no templates." },
            { t: "Own 500+ Nursery", d: "Premium plants grown at our own nursery for guaranteed quality and availability." },
            { t: "End-to-End Service", d: "Design, 3D visualisation, build, planting and maintenance under one roof." },
            { t: "Expert Team", d: "Horticulturists & landscape architects with 10+ years of hands-on experience." },
            { t: "On-Time Delivery", d: "Clear timelines, transparent quotes and accountable project management." },
            { t: "After-Care & AMC", d: "Year-round maintenance contracts so your garden always looks its best." },
          ].map((r, i) => (
            <div className="loc-why__card" key={i}>
              <span className="loc-why__num">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="loc-why__title">{r.t}</h3>
              <p className="loc-why__desc">{r.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="loc-section loc-faq">
        <span className="section-label">FAQ</span>
        <h2 className="section-title">{loc.city} — Frequently Asked Questions</h2>
        <span className="gold-divider" />
        <div className="loc-faq__list">
          {loc.faqs.map((f, i) => (
            <div key={i} className="loc-faq__item">
              <h3 className="loc-faq__q">{f.q}</h3>
              <p className="loc-faq__a">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── INTERNAL LINKS (other cities + key pages) ── */}
      <section className="loc-section loc-links">
        <h2 className="loc-block-title">Explore More</h2>
        <span className="gold-divider" />
        <div className="loc-links__row">
          <Link to="/services">All Services</Link>
          <Link to="/projects">Projects</Link>
          <Link to="/gallery">Gallery</Link>
          <Link to="/products">Plant Nursery</Link>
          <Link to="/about">About Us</Link>
          <Link to="/contact">Contact</Link>
        </div>
        {/* Other cities we serve */}
        <h3 className="loc-links__sub">Landscape Design &amp; Garden Care in Other Cities</h3>
        <div className="loc-links__row">
          {Object.values(LOCATION_BY_SLUG)
            .filter((o) => o.type === "city" && !o.national && o.citySlug !== loc.citySlug)
            .map((o) => (
              <Link key={o.slug} to={`/${o.slug}`}>{o.city}</Link>
            ))}
          <Link to="/service-areas" className="loc-links__all">All Service Areas →</Link>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="loc-cta">
        <h2 className="loc-cta__title">Ready to Transform Your Space in {loc.city}?</h2>
        <p className="loc-cta__text">
          Free consultation · Written quote within 24 hours · Open 7 days
        </p>
        <div className="loc-cta__actions">
          <a href={`tel:${PHONE}`} className="btn-luxury loc-rbtn">📞 Call +91 98000 40505</a>
          <a
            href={`https://wa.me/${PHONE.replace("+", "")}?text=${encodeURIComponent(WA_MSG)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="wa-btn"
          >
            <WhatsAppOutlined /> WhatsApp +91 98000 40505
          </a>
        </div>
        <button className="loc-hero__quote loc-cta__quote" onClick={() => navigate("/contact")}>
          or request a free quote online →
        </button>
      </section>
    </div>
  );
}
